import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCalendarAlt,
  faPlay,
  faStop,
  faPause,
  faTrash,
  faEdit,
  faPlus,
  faSpinner,
  faCheckCircle,
  faExclamationCircle,
  faClock,
  faHistory
} from '@fortawesome/free-solid-svg-icons';
import { api, getApiUrl } from '../utils/apiConfig';
import CreativeScheduleModal from './CreativeScheduleModal';
import CreativeScheduleHistory from './CreativeScheduleHistory';
import './CreativeScheduleList.css';

/**
 * CreativeScheduleList - Displays and manages creative schedules for a campaign
 */
const CreativeScheduleList = ({ campaign, accountId, user }) => {
  const [schedules, setSchedules] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [actionInProgress, setActionInProgress] = useState(null);
  const [historySchedule, setHistorySchedule] = useState(null);
  const [tooltip, setTooltip] = useState({ visible: false, content: '', x: 0, y: 0 });

  const handleMouseEnter = (e, content) => {
    const rect = e.target.getBoundingClientRect();
    setTooltip({
      visible: true,
      content,
      x: rect.left,
      y: rect.top - 8
    });
  };

  const handleMouseLeave = () => {
    setTooltip({ visible: false, content: '', x: 0, y: 0 });
  };

  // Fetch schedules
  const fetchSchedules = useCallback(async (silent = false) => {
    if (!campaign?.id || !accountId) return;

    try {
      if (!silent) setIsLoading(true);
      const response = await api.get(
        getApiUrl(`/api/v2/campaigns/${campaign.id}/creative-schedules`),
        { params: { accountId, userId: user?.userId || user?.id } }
      );
      setSchedules(response.data.schedules || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching creative schedules:', err);
      if (!silent) setError('Failed to load creative schedules');
    } finally {
      if (!silent) setIsLoading(false);
    }
  }, [campaign?.id, accountId, user]);

  // Initial fetch and polling
  useEffect(() => {
    fetchSchedules();

    // Poll every 5 seconds
    const interval = setInterval(() => {
      fetchSchedules(true);
    }, 5000);

    return () => clearInterval(interval);
  }, [fetchSchedules]);

  // Create schedule
  const handleCreateSchedule = async (scheduleData) => {
    try {
      setIsSubmitting(true);
      await api.post(
        getApiUrl(`/api/v2/campaigns/${campaign.id}/creative-schedules`),
        { ...scheduleData, accountId },
        { params: { accountId, userId: user?.userId || user?.id } }
      );
      setShowModal(false);
      fetchSchedules();
    } catch (err) {
      console.error('Error creating schedule:', err);
      alert('Failed to create schedule: ' + (err.response?.data?.error || err.message));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Update schedule
  const handleUpdateSchedule = async (scheduleData) => {
    if (!editingSchedule) return;
    
    try {
      setIsSubmitting(true);
      await api.patch(
        getApiUrl(`/api/v2/campaigns/${campaign.id}/creative-schedules/${editingSchedule.id}`),
        scheduleData,
        { params: { accountId, userId: user?.userId || user?.id } }
      );
      setShowModal(false);
      setEditingSchedule(null);
      fetchSchedules();
    } catch (err) {
      console.error('Error updating schedule:', err);
      alert('Failed to update schedule: ' + (err.response?.data?.error || err.message));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Delete schedule
  const handleDeleteSchedule = async (scheduleId) => {
    if (!window.confirm('Are you sure you want to delete this creative schedule?')) return;
    
    try {
      setActionInProgress(scheduleId);
      await api.delete(
        getApiUrl(`/api/v2/campaigns/${campaign.id}/creative-schedules/${scheduleId}`),
        { params: { accountId, userId: user?.userId || user?.id } }
      );
      fetchSchedules();
    } catch (err) {
      console.error('Error deleting schedule:', err);
      alert('Failed to delete schedule: ' + (err.response?.data?.error || err.message));
    } finally {
      setActionInProgress(null);
    }
  };

  // Enable now
  const handleEnableNow = async (scheduleId) => {
    try {
      setActionInProgress(scheduleId);
      await api.post(
        getApiUrl(`/api/v2/campaigns/${campaign.id}/creative-schedules/${scheduleId}/enable-now`),
        {},
        { params: { accountId, userId: user?.userId || user?.id } }
      );
      alert('Enable action triggered successfully');
      fetchSchedules();
    } catch (err) {
      console.error('Error triggering enable:', err);
      alert('Failed to trigger enable: ' + (err.response?.data?.error || err.message));
    } finally {
      setActionInProgress(null);
    }
  };

  // Disable now
  const handleDisableNow = async (scheduleId) => {
    try {
      setActionInProgress(scheduleId);
      await api.post(
        getApiUrl(`/api/v2/campaigns/${campaign.id}/creative-schedules/${scheduleId}/disable-now`),
        {},
        { params: { accountId, userId: user?.userId || user?.id } }
      );
      alert('Disable action triggered successfully');
      fetchSchedules();
    } catch (err) {
      console.error('Error triggering disable:', err);
      alert('Failed to trigger disable: ' + (err.response?.data?.error || err.message));
    } finally {
      setActionInProgress(null);
    }
  };

  const formatDateTime = (dateString, timeString, timezone) => {
    if (!dateString) return '-';
    // Parse date string as local date (not UTC) to avoid timezone shift
    // dateString is in YYYY-MM-DD format, so we split and create a local date
    const [year, month, day] = dateString.split('T')[0].split('-');
    const date = `${parseInt(month)}/${parseInt(day)}/${year}`;
    // Format time as HH:MM (remove seconds if present)
    const time = timeString ? timeString.substring(0, 5) : '00:00';
    // Get short timezone abbreviation (e.g., "America/Los_Angeles" -> "PT" or just show short form)
    const tz = timezone ? getTimezoneAbbr(timezone) : '';
    return `${date} ${time}${tz ? ` ${tz}` : ''}`;
  };

  // Get timezone abbreviation from IANA timezone
  const getTimezoneAbbr = (timezone) => {
    const abbrevMap = {
      'America/Los_Angeles': 'PT',
      'America/New_York': 'ET',
      'America/Chicago': 'CT',
      'America/Denver': 'MT',
      'Europe/London': 'GMT',
      'Europe/Paris': 'CET',
      'Asia/Tokyo': 'JST',
      'UTC': 'UTC'
    };
    return abbrevMap[timezone] || timezone?.split('/').pop()?.replace('_', ' ') || '';
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      scheduled: { icon: faClock, color: '#3b82f6', label: 'Scheduled', spin: false },
      processing: { icon: faSpinner, color: '#f59e0b', label: 'In Progress', spin: true },
      completed: { icon: faCheckCircle, color: '#10b981', label: 'Completed', spin: false },
      failed: { icon: faExclamationCircle, color: '#ef4444', label: 'Failed', spin: false },
      skipped: { icon: faPause, color: '#9ca3af', label: 'Skipped', spin: false }
    };
    const config = statusConfig[status] || statusConfig.scheduled;
    return (
      <span className="status-badge" style={{ color: config.color }}>
        <FontAwesomeIcon icon={config.icon} spin={config.spin} /> {config.label}
      </span>
    );
  };

  if (isLoading) {
    return (
      <div className="creative-schedule-section">
        <div className="section-header">
          <h3><FontAwesomeIcon icon={faCalendarAlt} /> Creative Scheduling</h3>
        </div>
        <div className="loading-state">
          <FontAwesomeIcon icon={faSpinner} spin /> Loading schedules...
        </div>
      </div>
    );
  }

  return (
    <div className="creative-schedule-section">
      <div className="section-header">
        <h3><FontAwesomeIcon icon={faCalendarAlt} /> Creative Scheduling</h3>
        <button
          className="btn btn-primary btn-sm"
          onClick={() => {
            setEditingSchedule(null);
            setShowModal(true);
          }}
        >
          <FontAwesomeIcon icon={faPlus} /> Add Schedule
        </button>
      </div>

      {error && (
        <div className="error-message">
          <FontAwesomeIcon icon={faExclamationCircle} /> {error}
        </div>
      )}

      {schedules.length === 0 ? (
        <div className="empty-state">
          <FontAwesomeIcon icon={faCalendarAlt} size="2x" style={{ color: '#9ca3af', marginBottom: '8px' }} />
          <p>No creative schedules configured</p>
          <p style={{ fontSize: '12px', color: '#6b7280' }}>
            Schedules are auto-created when ads are assigned with start/end dates
          </p>
        </div>
      ) : (
        <div className="schedules-table-container">
          <table className="schedules-table">
            <thead>
              <tr>
                <th>Ad Name</th>
                <th>Ad Set Name</th>
                <th>Notes</th>
                <th>Start Date/Time</th>
                <th>Start Status</th>
                <th>End Date/Time</th>
                <th>End Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {schedules.map(schedule => (
                <tr key={schedule.id}>
                  <td className="ad-name-cell">
                    <div className="ad-name">{schedule.adName || schedule.adId}</div>
                    <div className="ad-id">{schedule.adId}</div>
                  </td>
                  <td>{schedule.adsetName || '-'}</td>
                  <td className="notes-cell">
                    {schedule.notes ? (
                      <span
                        className="notes-text"
                        onMouseEnter={(e) => handleMouseEnter(e, schedule.notes)}
                        onMouseLeave={handleMouseLeave}
                      >
                        {schedule.notes.length > 50 ? `${schedule.notes.substring(0, 50)}...` : schedule.notes}
                      </span>
                    ) : '-'}
                  </td>
                  <td>{formatDateTime(schedule.startDate, schedule.startTime, schedule.timezone)}</td>
                  <td>{getStatusBadge(schedule.startStatus)}</td>
                  <td>{formatDateTime(schedule.endDate, schedule.endTime, schedule.timezone)}</td>
                  <td>{getStatusBadge(schedule.endStatus)}</td>
                  <td className="actions-cell">
                    {actionInProgress === schedule.id ? (
                      <FontAwesomeIcon icon={faSpinner} spin />
                    ) : (
                      <>
                        <button
                          className="btn-icon btn-success"
                          onClick={() => handleEnableNow(schedule.id)}
                          title={schedule.startStatus === 'processing' ? 'In progress...' : 'Enable Now'}
                          disabled={schedule.startStatus === 'processing'}
                        >
                          <FontAwesomeIcon icon={schedule.startStatus === 'processing' ? faSpinner : faPlay} spin={schedule.startStatus === 'processing'} />
                        </button>
                        <button
                          className="btn-icon btn-warning"
                          onClick={() => handleDisableNow(schedule.id)}
                          title={schedule.endStatus === 'processing' ? 'In progress...' : 'Disable Now'}
                          disabled={schedule.endStatus === 'processing'}
                        >
                          <FontAwesomeIcon icon={schedule.endStatus === 'processing' ? faSpinner : faStop} spin={schedule.endStatus === 'processing'} />
                        </button>
                        <button
                          className="btn-icon"
                          onClick={() => setHistorySchedule(schedule)}
                          title="View History"
                        >
                          <FontAwesomeIcon icon={faHistory} />
                        </button>
                        <button
                          className="btn-icon"
                          onClick={() => {
                            setEditingSchedule(schedule);
                            setShowModal(true);
                          }}
                          title="Edit Dates"
                        >
                          <FontAwesomeIcon icon={faEdit} />
                        </button>
                        <button
                          className="btn-icon btn-danger"
                          onClick={() => handleDeleteSchedule(schedule.id)}
                          title="Delete"
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create/Edit Modal */}
      <CreativeScheduleModal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setEditingSchedule(null);
        }}
        campaign={campaign}
        schedule={editingSchedule}
        onSubmit={editingSchedule ? handleUpdateSchedule : handleCreateSchedule}
        isSubmitting={isSubmitting}
      />

      {/* Execution History Modal */}
      <CreativeScheduleHistory
        isOpen={!!historySchedule}
        onClose={() => setHistorySchedule(null)}
        schedule={historySchedule}
        campaign={campaign}
        accountId={accountId}
        user={user}
      />

      {/* Notes Tooltip */}
      {tooltip.visible && (
        <div
          className="notes-tooltip-popup"
          style={{
            position: 'fixed',
            left: tooltip.x,
            top: tooltip.y,
            transform: 'translateY(-100%)'
          }}
        >
          {tooltip.content}
        </div>
      )}
    </div>
  );
};

export default CreativeScheduleList;

